/**
 * One website page as a stack of section strips, with the frames on top.
 * Used by the placement map editor (frames draggable / resizable), the
 * partnership page (campaign version) and, later, the booking page.
 */
import { useEffect, useRef, useState } from 'react';
import { MapFrame } from '../../lib/api/placementMap';

/** What the stack needs of a frame (the editor passes full MapFrames). */
export type StackFrame = Pick<MapFrame, 'id' | 'rate_key' | 'rate_label' | 'label' | 'x' | 'y' | 'w' | 'h'> & { one_slot?: boolean };

export interface StackSection {
  id: number;
  label: string;
  image: string;
  width: number;
  height: number;
  frames: StackFrame[];
}

type Box = Pick<MapFrame, 'x' | 'y' | 'w' | 'h'>;

interface Props {
  sections: StackSection[];
  selectedSectionId?: number | null;
  selectedFrameId?: number | null;
  onSelectSection?: (id: number) => void;
  onSelectFrame?: (frame: StackFrame, sectionId: number) => void;
  /** Editor: frames can be dragged and resized; the new box is reported on release. */
  editable?: boolean;
  onFrameMove?: (frameId: number, box: Box) => void;
  /** Only these rate keys stand out; other frames are dimmed. */
  highlight?: Set<string>;
  /** Hide frames that aren't highlighted instead of dimming them. */
  onlyHighlighted?: boolean;
  /** Booking page: every frame stays clearly clickable — LampTwist green,
   *  the highlighted (picked) one yellow (Doreen, 2026-10-22). */
  bookingLook?: boolean;
  /** Booking page: rate keys with no slot left — grey, labelled "Fully booked". */
  fullKeys?: Set<string>;
  fullLabel?: string;
  /** Tag on frames that mark one slot (translated on the booking page). */
  oneSlotLabel?: string;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

export default function SectionStack({
  sections, selectedSectionId, selectedFrameId, onSelectSection, onSelectFrame,
  editable, onFrameMove, highlight, onlyHighlighted, bookingLook, fullKeys, fullLabel = 'Fully booked', oneSlotLabel = '1 slot',
}: Props) {
  const [drag, setDrag] = useState<{
    id: number; mode: 'move' | 'resize'; sx: number; sy: number; start: Box; rect: DOMRect;
  } | null>(null);
  const [live, setLive] = useState<Record<number, Box>>({});
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const dx = ((e.clientX - drag.sx) / drag.rect.width) * 100;
      const dy = ((e.clientY - drag.sy) / drag.rect.height) * 100;
      const s = drag.start;
      const box = drag.mode === 'move'
        ? { ...s, x: clamp(s.x + dx, 0, 100 - s.w), y: clamp(s.y + dy, 0, 100 - s.h) }
        : { ...s, w: clamp(s.w + dx, 2, 100 - s.x), h: clamp(s.h + dy, 2, 100 - s.y) };
      setLive((prev) => ({ ...prev, [drag.id]: box }));
    };
    const up = () => {
      const box = liveRef.current[drag.id];
      if (box) {
        const r = (n: number) => Math.round(n * 100) / 100;
        onFrameMove?.(drag.id, { x: r(box.x), y: r(box.y), w: r(box.w), h: r(box.h) });
      }
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drag, onFrameMove]);

  // Once the saved data catches up, drop the local drag positions. Keyed on
  // the frames' saved boxes (not the array, which callers rebuild per render).
  const savedBoxes = sections.map((s) => s.frames.map((f) => `${f.id}:${f.x},${f.y},${f.w},${f.h}`).join(';')).join('|');
  useEffect(() => setLive((prev) => (Object.keys(prev).length ? {} : prev)), [savedBoxes]);

  const start = (e: React.PointerEvent, f: StackFrame, sectionId: number, mode: 'move' | 'resize') => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget.closest('.ss-sec') as HTMLElement).getBoundingClientRect();
    onSelectFrame?.(f, sectionId);
    setDrag({ id: f.id, mode, sx: e.clientX, sy: e.clientY, start: live[f.id] ?? f, rect });
  };

  return (
    <div className={`ss${bookingLook ? ' bk-look' : ''}`}>
      <style>{`
.ss { display: grid; gap: 0; border: 1px solid var(--border); border-radius: 10px; overflow: hidden; background: #f3f2ec; }
.ss-sec { position: relative; cursor: default; }
.ss-sec + .ss-sec { border-top: 1px dashed rgba(0,0,0,0.12); }
.ss-sec img { display: block; width: 100%; height: 100%; object-fit: cover; user-select: none; -webkit-user-drag: none; }
.ss-sec.sel { outline: 3px solid var(--sitewide); outline-offset: -3px; z-index: 1; }
.ss-tag { position: absolute; right: 8px; top: 8px; font-size: 10.5px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgba(255,255,255,0.92); color: var(--ink-3); pointer-events: none; }
.ss-fr { position: absolute; border: 2px solid #ffbd09; background: rgba(255,189,9,0.16); border-radius: 4px; }
.ss-fr.dim { border-color: rgba(43,65,56,0.25); background: rgba(43,65,56,0.04); }
.ss-fr.sel { border-color: var(--sitewide); background: rgba(59,92,255,0.14); z-index: 2; }
.ss-fr.edit { cursor: move; }
.ss-sec.pick .ss-fr:not(.dim) { cursor: pointer; }
.ss-sec.pick .ss-fr:not(.dim):hover { background: rgba(255,189,9,0.3); }
.ss-fr .lb { position: absolute; left: 4px; top: 4px; font-size: 10.5px; font-weight: 600; background: #ffbd09; color: #2b4138; padding: 1px 6px; border-radius: 4px; white-space: nowrap; max-width: calc(100% - 8px); overflow: hidden; text-overflow: ellipsis; pointer-events: none; }
.ss-fr.sel .lb { background: var(--sitewide); color: #fff; }
.ss-fr.dim .lb { display: none; }
.ss.bk-look .ss-fr { cursor: pointer; }
.ss.bk-look .ss-fr.dim { border: 2px solid #469e7a; background: rgba(70,158,122,0.16); }
.ss.bk-look .ss-fr.dim:hover { background: rgba(70,158,122,0.28); }
.ss.bk-look .ss-fr.dim .lb { display: block; background: #469e7a; color: #fff; }
.ss.bk-look .ss-fr:not(.dim) { border-width: 3px; background: rgba(255,189,9,0.22); box-shadow: 0 0 0 3px rgba(255,189,9,0.35); }
.ss.bk-look .ss-fr.full { border: 2px dashed #a8b6ad; background: rgba(43,65,56,0.10); box-shadow: none; }
.ss.bk-look .ss-fr.full .lb { display: block; background: #a8b6ad; color: #2b4138; }
/* One slot inside a bigger frame: stronger, tag at the bottom (2026-10-27). */
.ss-fr.one, .ss.bk-look .ss-fr.one:not(.dim) { border: 3px solid #d99a00; background: rgba(255,189,9,0.30); box-shadow: 0 0 0 2px rgba(255,255,255,0.7); z-index: 1; }
.ss-fr.one .lb, .ss.bk-look .ss-fr.one .lb { top: auto; bottom: 4px; display: block; background: #2b4138; color: #fff; }
.ss.bk-look .ss-fr.one.dim { border: 2px solid #2b6b52; background: rgba(70,158,122,0.22); }
.ss-fr.one.dim:not(.full) .lb { display: block; }
.ss-fr.one.sel { border-color: var(--sitewide); }
.ss-fr .rz { position: absolute; right: -5px; bottom: -5px; width: 12px; height: 12px; border-radius: 3px; background: var(--sitewide); cursor: nwse-resize; }
      `}</style>
      {sections.map((s) => (
        <div
          key={s.id}
          className={`ss-sec${selectedSectionId === s.id ? ' sel' : ''}${onSelectFrame && !editable ? ' pick' : ''}`}
          style={{ aspectRatio: `${s.width} / ${s.height}` }}
          onClick={() => onSelectSection?.(s.id)}
        >
          <img src={s.image} alt={s.label} loading="lazy" draggable={false} />
          {onSelectSection && <span className="ss-tag">{s.label}</span>}
          {s.frames.map((f) => {
            const on = !highlight || (!!f.rate_key && highlight.has(f.rate_key));
            if (!on && onlyHighlighted) return null;
            // Booking page: "1 slot" frames only on the picked placement, so the
            // map stays calm (Doreen, 2026-10-27).
            if (bookingLook && f.one_slot && !on) return null;
            const b = live[f.id] ?? f;
            const full = !!f.rate_key && !!fullKeys?.has(f.rate_key);
            return (
              <div
                key={f.id}
                className={`ss-fr${f.one_slot ? ' one' : ''}${on ? '' : ' dim'}${full ? ' full' : ''}${selectedFrameId === f.id ? ' sel' : ''}${editable ? ' edit' : ''}`}
                style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` }}
                title={f.rate_label ?? f.label}
                data-rate={f.rate_key ?? ''}
                onPointerDown={(e) => start(e, f, s.id, 'move')}
                onClick={(e) => { e.stopPropagation(); onSelectFrame?.(f, s.id); }}
              >
                <span className="lb">{f.one_slot ? oneSlotLabel : <>{f.rate_label ?? (f.label || 'Not linked yet')}{full ? ` · ${fullLabel}` : ''}</>}</span>
                {editable && selectedFrameId === f.id && (
                  <span className="rz" onPointerDown={(e) => start(e, f, s.id, 'resize')} />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
